import { X, CheckCircle, Calendar, Clock, Package, MapPin } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface PickupConfirmationData {
  confirmationNumber: string;
  date: string;
  timeSlot: string;
  itemName: string;
  itemPrice?: number;
  address: string;
}

interface PickupConfirmationProps {
  data: PickupConfirmationData;
  onDismiss: () => void;
  autoDismissMs?: number;
}

export default function PickupConfirmation({
  data,
  onDismiss,
  autoDismissMs = 30000, // 30 seconds for important confirmation
}: PickupConfirmationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Fade in
    setTimeout(() => setIsVisible(true), 50);

    // Progress bar countdown
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / autoDismissMs) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        handleDismiss();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [autoDismissMs]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  return (
    <div
      className={`fixed bottom-24 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 border border-green-200 rounded-xl shadow-2xl shadow-black/20 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-green-100 rounded-t-xl overflow-hidden">
        <div
          className="h-full bg-green-500 transition-all duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Success header */}
      <div className="flex items-center gap-3 p-4 pb-2">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Ophaalafspraak bevestigd</h3>
          <p className="text-sm text-muted-foreground">Meubel retour</p>
        </div>
        <button
          onClick={handleDismiss}
          className="ml-auto p-1 hover:bg-muted rounded-full transition-colors"
          aria-label="Sluiten"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Confirmation number - prominent */}
      <div className="mx-4 mb-3 bg-primary/10 rounded-lg p-3">
        <p className="text-xs text-primary/80 mb-1">Referentienummer</p>
        <p className="text-xl font-bold text-primary tracking-wider">{data.confirmationNumber}</p>
      </div>

      {/* Details */}
      <div className="px-4 pb-3 space-y-2.5">
        {/* Date */}
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium capitalize">{data.date}</span>
        </div>

        {/* Time */}
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span>{data.timeSlot}</span>
        </div>

        {/* Item */}
        <div className="flex items-center gap-2 text-sm">
          <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate">{data.itemName}</span>
          {data.itemPrice && (
            <span className="text-muted-foreground ml-auto">€{data.itemPrice.toFixed(2)}</span>
          )}
        </div>

        {/* Address */}
        {data.address && (
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <span className="text-muted-foreground">{data.address}</span>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="px-4 pb-4">
        <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-2">
          Je ontvangt een bevestiging per e-mail. Zorg dat het meubel klaar staat op de ophaaldatum.
        </p>
      </div>
    </div>
  );
}
